import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Card, useTheme, useMediaQuery } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SmartReport3DCard from "../components/SmartReport3DCard";
import VizApp3DCard from "../components/VizApp3DCard";

/**
 * Next-Generation Smart Reporting Section
 * 
 * Features Smart Report PDF and Viz App cards with preview buttons
 */
const NextGenReportingSection = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        width: "100%",
        padding: {
          xs: "60px 16px",
          sm: "80px 24px",
          md: "100px 48px",
        },
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        {/* Section Header */}
        <Box
          sx={{
            textAlign: "center",
            marginBottom: { xs: "40px", md: "60px" },
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: "28px", sm: "36px", md: "48px" },
              fontWeight: 700,
              fontFamily: "'Poppins', sans-serif",
              color: "#022674",
              marginBottom: { xs: "16px", md: "24px" },
            }}
          >
            Next-Generation Smart Reporting
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              color: "#64748b",
              lineHeight: 1.7,
              maxWidth: "900px",
              margin: "0 auto",
            }}
          >
            Choose between Smart Report PDF and Viz App digital reporting — two powerful formats built for patient clarity and clinical intelligence. Transform complex medical data into actionable health insights.
          </Typography>
        </Box>

        {/* Cards Grid */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "column", md: "row" },
            gap: { xs: "32px", sm: "40px", md: "48px" },
            margin: "0 auto",
            width: { xs: "100%", sm: "100%", md: "80%" },
            justifyContent: "center",
            alignItems: { xs: "stretch", md: "stretch" },
          }}
        >
          {/* Smart Report Card */}
          <Card
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              transition: "all 0.3s ease",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              border: "1px solid rgba(53, 103, 214, 0.15)",
              background: "#ffffff",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(53, 103, 214, 0.25)",
              },
            }}
          >
            {/* 3D Animated Smart Report Card */}
            <Box
              sx={{
                width: "100%",
                height: { xs: "250px", md: "350px" },
                borderBottom: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            >
              <SmartReport3DCard />
            </Box>
            
            {/* Card Content */}
            <Box
              sx={{
                padding: { xs: "24px", md: "32px" },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: { xs: "180px", md: "200px" },
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "22px", md: "28px" },
                    fontWeight: 700,
                    fontFamily: "'Poppins', sans-serif",
                    color: "#022674",
                    marginBottom: "12px",
                  }}
                >
                  Smart Report
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "14px", md: "16px" },
                    color: "#64748b",
                    lineHeight: 1.6,
                    marginBottom: 0,
                  }}
                >
                  Professional PDF reporting with clear visualizations and patient-friendly explanations.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate("/smartReport")}
                sx={{
                  background: "linear-gradient(135deg, #3567D6 0%, #1E4DB7 100%)",
                  color: "white",
                  fontSize: { xs: "14px", md: "16px" },
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  padding: { xs: "10px 24px", md: "12px 32px" },
                  borderRadius: "10px",
                  textTransform: "none",
                  alignSelf: "flex-start",
                  marginTop: "24px",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1E4DB7 0%, #022674 100%)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Preview
              </Button>
            </Box>
          </Card>

          {/* Viz App Card */}
          <Card
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              border: "1px solid rgba(53, 103, 214, 0.15)",
              background: "#ffffff",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-8px)",
                boxShadow: "0 12px 48px rgba(0, 0, 0, 0.15)",
                border: "1px solid rgba(53, 103, 214, 0.25)",
              },
            }}
          >
            {/* 3D Animated Viz App Card */}
            <Box
              sx={{
                width: "100%",
                height: { xs: "250px", md: "350px" },
                borderBottom: "1px solid #e2e8f0",
                flexShrink: 0,
              }}
            >
              <VizApp3DCard />
            </Box>
            
            {/* Card Content */}
            <Box
              sx={{
                padding: { xs: "24px", md: "32px" },
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: { xs: "180px", md: "200px" },
                justifyContent: "space-between",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: "22px", md: "28px" },
                    fontWeight: 700,
                    fontFamily: "'Poppins', sans-serif",
                    color: "#022674",
                    marginBottom: "12px",
                  }}
                >
                  Viz App
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "14px", md: "16px" },
                    color: "#64748b",
                    lineHeight: 1.6,
                    marginBottom: 0,
                  }}
                >
                  Interactive digital reports with audio insights, real-time analytics, and mobile-first design.
                </Typography>
              </Box>
              <Button
                variant="contained"
                startIcon={<VisibilityIcon />}
                onClick={() => navigate("/vizApp")}
                sx={{
                  background: "linear-gradient(135deg, #3567D6 0%, #1E4DB7 100%)",
                  color: "white",
                  fontSize: { xs: "14px", md: "16px" },
                  fontWeight: 600,
                  fontFamily: "'Poppins', sans-serif",
                  padding: { xs: "10px 24px", md: "12px 32px" },
                  borderRadius: "10px",
                  textTransform: "none",
                  alignSelf: "flex-start",
                  marginTop: "24px",
                  "&:hover": {
                    background: "linear-gradient(135deg, #1E4DB7 0%, #022674 100%)",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Preview
              </Button>
            </Box>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default NextGenReportingSection;

